import { Component } from "@theme/component";
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
} from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );
    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      {
        signal,
      }
    );

    // Setup image loading observers
    this.#setupImageLoadingObserver();

    // Listen for variant image preloading events
    document.addEventListener(
      "variant-image:loaded",
      this.#handleImagePreloaded,
      { signal }
    );
  }

  #controller = new AbortController();

  /** @type {Map<string, HTMLElement>} */
  #imageLoadingStates = new Map();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#imageLoadingStates.clear();
  }

  /**
   * Sets up observers for image loading states
   */
  #setupImageLoadingObserver() {
    // Find all images in the gallery and add loading indicators
    const images = this.querySelectorAll(".product-media__image");

    images.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        // Image already loaded
        this.#markImageAsLoaded(img);
      } else {
        // Add loading state
        this.#markImageAsLoading(img);

        img.addEventListener("load", () => this.#markImageAsLoaded(img), {
          once: true,
        });
        img.addEventListener("error", () => this.#markImageAsError(img), {
          once: true,
        });
      }
    });
  }

  /**
   * Marks an image container as loading
   * @param {HTMLImageElement} img - The image element
   */
  #markImageAsLoading(img) {
    const container = img.closest(".product-media-container");
    if (container && !container.hasAttribute("data-image-loaded")) {
      container.setAttribute("data-image-loading", "");
      this.#imageLoadingStates.set(img.src, container);
    }
  }

  /**
   * Marks an image as loaded
   * @param {HTMLImageElement} img - The image element
   */
  #markImageAsLoaded(img) {
    const container = img.closest(".product-media-container");
    if (container) {
      container.removeAttribute("data-image-loading");
      container.setAttribute("data-image-loaded", "");
      this.#imageLoadingStates.delete(img.src);
    }
  }

  /**
   * Marks an image as having an error
   * @param {HTMLImageElement} img - The image element
   */
  #markImageAsError(img) {
    const container = img.closest(".product-media-container");
    if (container) {
      container.removeAttribute("data-image-loading");
      container.setAttribute("data-image-error", "");
      this.#imageLoadingStates.delete(img.src);
    }
  }

  /**
   * Handles the preloaded image event
   * @param {CustomEvent} event - The custom event with image details
   */
  #handleImagePreloaded = (event) => {
    const { src } = event.detail;

    // Find any images with this src and mark them as loaded
    const images = this.querySelectorAll(
      `img[src="${src}"], img[src*="${src}"]`
    );
    images.forEach((img) => {
      if (img instanceof HTMLImageElement) {
        this.#markImageAsLoaded(img);
      }
    });
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    // The new gallery will initialize its own loading observers when connected
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
